/* Keep one validated render path for native products, care bundles, quantity changes and empty carts. */
class CartDrawer extends HTMLElement {
  constructor() {
    super(); this.refreshVersion = 0;
    this.addEventListener('keyup', event => {if (event.code === 'Escape') this.close();});
    this.addEventListener('click', event => {if (event.target.id === 'CartDrawer-Overlay') this.close();});
    this.setHeaderCartIconAccessibility();
  }
  setHeaderCartIconAccessibility() {
    const link = document.getElementById('cart-icon-bubble');
    if (!link) return;
    link.setAttribute('role', 'button'); link.setAttribute('aria-haspopup', 'dialog');
    link.addEventListener('click', event => {event.preventDefault(); this.open(link);});
    link.addEventListener('keydown', event => {if (event.code.toUpperCase() === 'SPACE') {event.preventDefault(); this.open(link);}});
  }
  open(triggeredBy) {
    if (triggeredBy) this.setActiveElement(triggeredBy);
    const note = this.querySelector('[id^="Details-"] summary');
    if (note && !note.hasAttribute('role')) this.setSummaryAccessibility(note);
    this.classList.add('animate', 'active'); document.body.classList.add('overflow-hidden');
    requestAnimationFrame(() => {
      const inner = this.querySelector('.drawer__inner');
      if (inner && typeof trapFocus === 'function') trapFocus(inner, inner.querySelector('.drawer__close') || inner);
    });
  }
  close() {this.classList.remove('active'); if (typeof removeTrapFocus === 'function') removeTrapFocus(this.activeElement); document.body.classList.remove('overflow-hidden');}
  setSummaryAccessibility(summary) {
    summary.setAttribute('role', 'button'); summary.setAttribute('aria-expanded', String(summary.closest('details').open));
    if (summary.nextElementSibling?.id) summary.setAttribute('aria-controls', summary.nextElementSibling.id);
    summary.addEventListener('click', () => summary.setAttribute('aria-expanded', String(!summary.closest('details').open)));
  }
  prepare(html) {
    if (typeof html !== 'string' || !html.trim()) throw Error('The cart display could not be loaded.');
    const doc = new DOMParser().parseFromString(html, 'text/html');
    const host = doc.querySelector('cart-drawer');
    const inner = doc.querySelector('#CartDrawer .drawer__inner') || doc.querySelector('.drawer__inner');
    if (!inner || !inner.querySelector('.drawer__header') || !inner.querySelector('#CartDrawer-Form')) throw Error('The cart display is incomplete.');
    // Optional presentation errors must not block valid cart items and totals.
    for (const optional of inner.querySelectorAll('[data-jt-cart-shipping], justtry-cart-recommendations')) {
      if (optional.innerHTML.includes('Liquid error')) {
        console.warn('[JustTry cart] Optional cart module contains a Liquid error.');
        optional.replaceChildren(); optional.hidden = true;
      }
    }
    for (const required of inner.querySelectorAll('#CartDrawer-Form, .cart-drawer__footer')) {
      if (required.innerHTML.includes('Liquid error')) throw Error('Cart items or totals contain a Liquid error.');
    }
    const count = host?.getAttribute('data-cart-count');
    const empty = count !== null && count !== undefined ? Number(count) === 0 : host ? host.classList.contains('is-empty') : Boolean(inner.querySelector('.jt-cart-empty'));
    if (empty && !inner.querySelector('.jt-cart-empty, .drawer__inner-empty')) throw Error('The empty cart display is incomplete.');
    return {doc, inner, empty};
  }
  commit(prepared, state, open) {
    const current = this.querySelector('.drawer__inner');
    if (!current) throw Error('The cart container is missing.');
    const fresh = document.importNode(prepared.inner, true);
    const previousFooter = current.querySelector('.drawer__footer');
    const freshFooter = fresh.querySelector('.drawer__footer');
    if (previousFooter && freshFooter) {
      // Keep the live footer, native checkout button and app-owned Seel nodes/listeners.
      const nextTotals = freshFooter.querySelector('.cart-drawer__footer');
      const oldTotals = previousFooter.querySelector('.cart-drawer__footer');
      if (oldTotals && nextTotals) oldTotals.replaceWith(nextTotals);
      const oldTools = previousFooter.querySelectorAll('.jt-cart-tool');
      freshFooter.querySelectorAll('.jt-cart-tool').forEach((tool, index) => oldTools[index]?.replaceWith(tool));
      const checkout = previousFooter.querySelector('#CartDrawer-Checkout');
      if (checkout) checkout.disabled = prepared.empty;
      freshFooter.replaceWith(previousFooter);
    }
    current.replaceChildren(...fresh.childNodes);
    this.classList.toggle('is-empty', prepared.empty);
    this.dataset.jtCartEmpty = String(prepared.empty);
    if (prepared.doc.querySelector('cart-drawer')?.hasAttribute('data-cart-count')) this.dataset.cartCount = prepared.doc.querySelector('cart-drawer').dataset.cartCount;
    current.classList.toggle('is-empty', prepared.empty);
    this.querySelectorAll('cart-drawer-items').forEach(owner => {
      owner.classList.toggle('is-empty', prepared.empty);
      owner.lineItemStatusElement = owner.querySelector('#CartDrawer-LineItemStatus');
    });
    const iconHTML = state.sections?.['cart-icon-bubble'] || prepared.doc.getElementById('cart-icon-bubble')?.outerHTML;
    if (typeof iconHTML === 'string') {
      const iconDoc = new DOMParser().parseFromString(iconHTML, 'text/html');
      const source = iconDoc.getElementById('cart-icon-bubble') || iconDoc.querySelector('.shopify-section');
      const target = document.getElementById('cart-icon-bubble');
      if (source && target) target.innerHTML = source.innerHTML;
    }
    this.querySelector('.jt-cart-render-error')?.remove();
    if (open) this.open();
    document.dispatchEvent(new CustomEvent('justtry:cart-rendered', {detail: {empty:prepared.empty}}));
    return true;
  }
  renderContents(state, {open = true} = {}) {
    ++this.refreshVersion; // Discard an older background GET response.
    try {return Promise.resolve(this.commit(this.prepare(state.sections?.['cart-drawer']), state, open));}
    catch (error) {console.warn('[JustTry cart] Mutation response could not render:', error.message); return this.refresh({open});}
  }
  async refresh({open = false} = {}) {
    const version = ++this.refreshVersion;
    // This theme deliberately omits the drawer on /cart. Read it from the current page.
    const pageURL = new URL(window.location.href);
    pageURL.hash = '';
    pageURL.searchParams.delete('sections');
    pageURL.searchParams.delete('section_id');
    let prepared;
    try {
      // The normal current-page read is proven to work for this theme; do not retry unusable section reads first.
      const response = await fetch(pageURL.href, {headers:{Accept:'text/html'}, cache:'no-store', credentials:'same-origin'});
      if (!response.ok) throw Error('Current page HTTP ' + response.status);
      prepared = this.prepare(await response.text());
    } catch (error) {
      if (version !== this.refreshVersion) return false;
      console.error('[JustTry cart] Refresh failed:', error.message);
      this.showError('Your cart display could not update. Please try again.');
      if (open) this.open();
      return false;
    }
    if (version !== this.refreshVersion) return false;
    try {return this.commit(prepared, {}, open);}
    catch (error) {
      console.error('[JustTry cart] DOM update failed:', error.message);
      this.showError('Your cart display could not update. Please try again.');
      if (open) this.open();
      return false;
    }
  }

  showError(message) {
    let box = this.querySelector('.jt-cart-render-error');
    if (!box) {box = document.createElement('div'); box.className = 'jt-cart-render-error'; box.setAttribute('role', 'alert'); this.querySelector('.drawer__header')?.after(box);}
    box.replaceChildren(document.createTextNode(message + ' '));
    const retry = document.createElement('button'); retry.type = 'button'; retry.textContent = 'Retry';
    retry.addEventListener('click', () => this.refresh({open:true})); box.append(retry);
  }
  getSectionInnerHTML(html, selector = '.shopify-section') {return new DOMParser().parseFromString(html, 'text/html').querySelector(selector)?.innerHTML || '';}
  getSectionsToRender() {return [{id:'cart-drawer', selector:'#CartDrawer'}, {id:'cart-icon-bubble'}];}
  getSectionDOM(html, selector = '.shopify-section') {return new DOMParser().parseFromString(html, 'text/html').querySelector(selector);}
  setActiveElement(element) {this.activeElement = element;}
}
customElements.define('cart-drawer', CartDrawer);

class CartDrawerItems extends CartItems {
  getSectionsToRender() {return [{id:'CartDrawer', section:'cart-drawer', selector:'.drawer__inner'}, {id:'cart-icon-bubble', section:'cart-icon-bubble', selector:'.shopify-section'}];}
  onChange(event) {
    if (!event.target.matches('.quantity__input')) return;
    this.updateQuantity(event.target.dataset.index, event.target.value, event.target.name, event.target.dataset.quantityVariantId);
  }
  async updateQuantity(line, quantity, name, variantId) {
    const drawer = this.closest('cart-drawer');
    if (!drawer || drawer.dataset.jtUpdating === 'true') return;
    const row = this.querySelector('#CartDrawer-Item-' + line);
    const key = row?.dataset.cartKey;
    drawer.dataset.jtUpdating = 'true';
    const controls = [...drawer.querySelectorAll('cart-drawer-items button,cart-drawer-items input')].filter(control => !control.disabled);
    controls.forEach(control => control.disabled = true);
    try {
      const q = Number(quantity);
      if (!Number.isInteger(q) || q < 0) throw Error('Please choose a valid quantity.');
      const body = {quantity:q, sections:['cart-drawer','cart-icon-bubble'], sections_url:window.location.pathname};
      if (key) body.id = key; else body.line = Number(line);
      const response = await fetch(routes.cart_change_url, {...fetchConfig(), body:JSON.stringify(body)});
      const state = await response.json();
      if (!response.ok || state.errors || state.status) throw Error(state.description || state.message || state.errors || window.cartStrings?.error || 'The cart could not update.');
      await drawer.renderContents(state, {open:false});
      if (typeof publish === 'function') publish(PUB_SUB_EVENTS.cartUpdate, {source:'cart-items',cartData:state,variantId});
    } catch (error) {drawer.showError(typeof error.message === 'string' ? error.message : 'The cart could not update.');}
    finally {delete drawer.dataset.jtUpdating; controls.forEach(control => control.disabled = false);}
  }
}
customElements.define('cart-drawer-items', CartDrawerItems);

// Retain this theme API for other uses, though the new drawer has no separate recommendation column.
if (!customElements.get('subcart-drawer')) customElements.define('subcart-drawer', class extends HTMLElement {
  connectedCallback() {this.querySelector('#sub_d_close')?.addEventListener('click', () => this.closeSubDrawer());}
  closeSubDrawer() {this.classList.remove('active');}
  openSubDrawer() {this.classList.add('animate','active');}
});
